import { datePl, type MeDto, PLACED_METHODS, PO_STATUSES, type PoDto, type PoStatus, type PurchaseSuggestionDto, type ShortageByItemDto, type SupplierDto, type SupplierInput, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Checkbox, Drawer, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconPlus, IconTruckDelivery } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { Money, MoneyInput } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { ItemSelect, useStockItems, WarehouseSelect } from './common';
import { useWarehouses } from '../../data';

const newKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k${Date.now()}${Math.random()}`);
const STATUS_COLOR: Record<PoStatus, string> = { robocze: 'gray', zlozone: 'blue', czesciowo_dostarczone: 'yellow', dostarczone: 'green', zamkniete: 'orange', anulowane: 'gray' };

export function PoStatusBadge({ status, late }: { status: PoStatus; late?: boolean }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Badge variant="light" color={STATUS_COLOR[status]}>
        {PO_STATUSES[status]}
      </Badge>
      {late && (
        <Badge variant="light" color="red">
          po terminie
        </Badge>
      )}
    </Group>
  );
}

function useSuppliers() {
  return useQuery({ queryKey: ['suppliers'], queryFn: () => api<SupplierDto[]>('/suppliers'), staleTime: 60_000 });
}

/** Zamówienia do dostawców i dostawcy (docs/14 §2.9). Zamówienie zabezpiecza braki; przyjęcie dostawy zwiększa stan i rezerwuje dla potrzeb. */
export function OrdersPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const view = params.get('widok') ?? 'otwarte';
  const openId = params.get('zamowienie');
  const [editing, setEditing] = useState<PoDto | 'new' | OrderDraft | null>(null);
  const q = useQuery({
    queryKey: ['purchase-orders', view],
    queryFn: () => api<PoDto[]>(view === 'otwarte' ? '/purchase-orders?open=true' : '/purchase-orders'),
    enabled: view !== 'dostawcy' && view !== 'podpowiedzi',
  });
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const canOrder = can(me, 'zakupy.zamowienia');
  return (
    <>
      <PageHeader
        title="Zamówienia do dostawców"
        description="Zamówienie zabezpiecza braki z zapotrzebowań — ten sam towar nie pokryje dwóch braków. Dostawy mogą być częściowe; przyjęty towar trafia najpierw do realizacji z najwcześniejszym terminem."
        actions={
          canOrder && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              Nowe zamówienie
            </Button>
          )
        }
      />
      <SegmentedControl
        mb="md"
        value={view}
        onChange={(v) => set('widok', v === 'otwarte' ? null : v)}
        data={[
          { value: 'otwarte', label: 'Otwarte' },
          { value: 'wszystkie', label: 'Wszystkie' },
          { value: 'podpowiedzi', label: 'Podpowiedzi zakupowe' },
          { value: 'dostawcy', label: 'Dostawcy' },
        ]}
      />
      {view === 'dostawcy' ? (
        <SuppliersSection me={me} />
      ) : view === 'podpowiedzi' ? (
        <SuggestionsSection canOrder={canOrder} onOrder={(d) => setEditing(d)} />
      ) : (
        <QueryState isLoading={q.isLoading} error={q.error}>
          {q.data?.length === 0 ? (
            <EmptyState title="Brak zamówień" hint={canOrder ? 'Utwórz zamówienie z braków w zapotrzebowaniach albo ręcznie.' : undefined} icon={<IconTruckDelivery size={28} />} />
          ) : (
            <SectionCard padding={0}>
              <Table.ScrollContainer minWidth={760}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Numer</Table.Th>
                      <Table.Th>Dostawca</Table.Th>
                      <Table.Th>Magazyn</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Termin</Table.Th>
                      <Table.Th ta="right">Pozycje</Table.Th>
                      <Table.Th ta="right">Wartość</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {q.data?.map((o) => (
                      <Table.Tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => set('zamowienie', o.id)}>
                        <Table.Td fw={500}>{o.number}</Table.Td>
                        <Table.Td>{o.supplierName}</Table.Td>
                        <Table.Td>
                          {o.companyCode} · {o.warehouseCode}
                        </Table.Td>
                        <Table.Td>
                          <PoStatusBadge status={o.status} late={o.late} />
                        </Table.Td>
                        <Table.Td>{o.expectedDate ? datePl(o.expectedDate) : '—'}</Table.Td>
                        <Table.Td ta="right">{o.lines.length}</Table.Td>
                        <Table.Td ta="right">{o.valueGr === null ? '—' : <Money gr={o.valueGr} />}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </SectionCard>
          )}
        </QueryState>
      )}
      {openId && <OrderDrawer id={openId} me={me} onClose={() => set('zamowienie', null)} onEdit={(o) => setEditing(o)} />}
      {editing && <OrderForm order={editing === 'new' || 'draft' in editing ? null : editing} draft={editing !== 'new' && 'draft' in editing ? editing : undefined} onClose={() => setEditing(null)} onSaved={(o) => (setEditing(null), set('zamowienie', o.id))} />}
    </>
  );
}

function OrderDrawer({ id, me, onClose, onEdit }: { id: string; me: MeDto; onClose: () => void; onEdit: (o: PoDto) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase-order', id], queryFn: () => api<PoDto>(`/purchase-orders/${id}`) });
  const [modal, setModal] = useState<'place' | 'receive' | 'close' | 'cancel' | null>(null);
  const done = (o: PoDto, msg: string) => {
    qc.setQueryData(['purchase-order', id], o);
    void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    void qc.invalidateQueries({ queryKey: ['demands'] });
    void qc.invalidateQueries({ queryKey: ['stock'] });
    void qc.invalidateQueries({ queryKey: ['purchase-suggestions'] });
    void qc.invalidateQueries({ queryKey: ['shortages'] });
    setModal(null);
    notifyOk(msg);
  };
  const o = q.data;
  const open = o && ['zlozone', 'czesciowo_dostarczone'].includes(o.status);
  return (
    <Drawer opened onClose={onClose} position="right" size="xl" title={o ? `Zamówienie ${o.number}` : 'Zamówienie'}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {o && (
          <Stack>
            <Group justify="space-between">
              <PoStatusBadge status={o.status} late={o.late} />
              <Group gap={6}>
                {o.status === 'robocze' && can(me, 'zakupy.zamowienia') && (
                  <Button size="xs" variant="default" onClick={() => onEdit(o)}>
                    Edytuj
                  </Button>
                )}
                {o.status === 'robocze' && can(me, 'zakupy.zlozenie') && (
                  <Button size="xs" onClick={() => setModal('place')}>
                    Złóż u dostawcy
                  </Button>
                )}
                {open && can(me, 'magazyn.ruchy') && (
                  <Button size="xs" onClick={() => setModal('receive')}>
                    Przyjmij dostawę
                  </Button>
                )}
                {open && can(me, 'zakupy.zamowienia') && (
                  <Button size="xs" variant="subtle" color="orange" onClick={() => setModal('close')}>
                    Zamknij z brakiem
                  </Button>
                )}
                {['robocze', 'zlozone'].includes(o.status) && o.lines.every((l) => l.received === 0) && can(me, 'zakupy.zamowienia') && (
                  <Button size="xs" variant="subtle" color="red" onClick={() => setModal('cancel')}>
                    Anuluj
                  </Button>
                )}
              </Group>
            </Group>
            <SectionCard padding="sm">
              <Stack gap={4}>
                <Text size="sm">
                  Dostawca: <b>{o.supplierName}</b> · magazyn {o.companyCode} · {o.warehouseCode}
                </Text>
                <Text size="sm">Oczekiwany termin: {o.expectedDate ? datePl(o.expectedDate) : 'nie podano'}</Text>
                {o.placedAt && (
                  <Text size="sm">
                    Złożone {formatDateTime(o.placedAt)} ({o.placedMethod ? PLACED_METHODS[o.placedMethod] : ''}) przez {o.placedByName ?? '—'}
                    {o.placedNote ? ` — ${o.placedNote}` : ''}
                  </Text>
                )}
                {o.closedReason && <Text size="sm">Przyczyna zamknięcia/anulowania: {o.closedReason}</Text>}
                {o.note && (
                  <Text size="sm" c="dimmed">
                    {o.note}
                  </Text>
                )}
              </Stack>
            </SectionCard>
            <SectionCard title="Pozycje" padding="sm">
              <Table.ScrollContainer minWidth={600}>
                <Table verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Produkt</Table.Th>
                      <Table.Th ta="right">Zamówiono</Table.Th>
                      <Table.Th ta="right">Dostarczono</Table.Th>
                      <Table.Th ta="right">Pozostało</Table.Th>
                      <Table.Th ta="right">Cena</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {o.lines.map((l) => (
                      <Table.Tr key={l.id}>
                        <Table.Td>
                          <Text size="sm">{l.name}</Text>
                          {l.packName && (
                            <Text size="xs" c="dimmed">
                              {qtyPl(l.packQty ?? 0)} × {l.packName}
                            </Text>
                          )}
                          {l.allocations.map((a) => (
                            <Text key={a.id} size="xs" c="dimmed">
                              dla{' '}
                              {a.demandId ? (
                                <Anchor component={Link} to={`/magazyn/zapotrzebowania?zapotrzebowanie=${a.demandId}`} size="xs">
                                  {a.caseNumber ?? '—'}
                                  {a.taskNumber ? ` / ${a.taskNumber}` : ''}
                                </Anchor>
                              ) : (
                                <>
                                  {a.caseNumber ?? '—'}
                                  {a.taskNumber ? ` / ${a.taskNumber}` : ''}
                                </>
                              )}
                              : {qtyPl(a.quantity)} {l.unit}
                              {a.neededBy ? ` (na ${datePl(a.neededBy)})` : ''}
                            </Text>
                          ))}
                        </Table.Td>
                        <Table.Td ta="right">
                          {qtyPl(l.quantity)} {l.unit}
                        </Table.Td>
                        <Table.Td ta="right">{qtyPl(l.received)}</Table.Td>
                        <Table.Td ta="right">{l.cancelled ? `0 (anul. ${qtyPl(l.cancelled)})` : qtyPl(l.remaining)}</Table.Td>
                        <Table.Td ta="right">{l.unitPriceGr === null ? '—' : <Money gr={l.unitPriceGr} />}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </SectionCard>
            {o.receipts.length > 0 && (
              <SectionCard title="Dostawy" padding="sm">
                {o.receipts.map((r) => (
                  <Text key={r.documentId} size="sm">
                    <Anchor component={Link} to={`/magazyn/dokumenty?dokument=${r.documentId}`}>
                      {r.number}
                    </Anchor>{' '}
                    z {datePl(r.docDate)}
                    {r.externalNumber ? ` · dok. dostawcy ${r.externalNumber}` : ''}
                  </Text>
                ))}
              </SectionCard>
            )}
          </Stack>
        )}
      </QueryState>
      {o && modal === 'place' && <PlaceModal order={o} onClose={() => setModal(null)} onDone={(r) => done(r, 'Zamówienie złożone')} />}
      {o && modal === 'receive' && <ReceiveModal order={o} onClose={() => setModal(null)} onDone={(r) => done(r, 'Przyjęto dostawę')} />}
      {o && (modal === 'close' || modal === 'cancel') && (
        <ReasonModal
          title={modal === 'close' ? 'Zamknij zamówienie z brakiem' : 'Anuluj zamówienie'}
          hint={modal === 'close' ? 'Niedostarczona reszta przestaje zabezpieczać braki — wrócą jako braki do zamówienia.' : 'Zamówienie przestanie zabezpieczać braki.'}
          path={`/purchase-orders/${o.id}/${modal}`}
          onClose={() => setModal(null)}
          onDone={(r) => done(r, modal === 'close' ? 'Zamknięto zamówienie' : 'Anulowano zamówienie')}
        />
      )}
    </Drawer>
  );
}

function PlaceModal({ order, onClose, onDone }: { order: PoDto; onClose: () => void; onDone: (o: PoDto) => void }) {
  const [method, setMethod] = useState<string>('email');
  const [note, setNote] = useState('');
  const [expectedDate, setExpectedDate] = useState(order.expectedDate ?? '');
  const m = useMutation({
    mutationFn: () => api<PoDto>(`/purchase-orders/${order.id}/place`, { method: 'POST', body: { method, note, expectedDate: expectedDate || null } }),
    onSuccess: onDone,
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Złożenie zamówienia u dostawcy">
      <Stack>
        <Text size="sm" c="dimmed">
          System nie wysyła zamówienia — złóż je u dostawcy i odnotuj tutaj, jak to zrobiono.
        </Text>
        <Select label="Sposób" data={Object.entries(PLACED_METHODS).map(([value, label]) => ({ value, label }))} value={method} onChange={(v) => setMethod(v ?? 'email')} allowDeselect={false} />
        <TextInput label="Potwierdzony termin dostawy" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.currentTarget.value)} />
        <TextInput label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} placeholder="np. potwierdzenie nr, osoba kontaktowa" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending}>
            Złożone
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ReceiveModal({ order, onClose, onDone }: { order: PoDto; onClose: () => void; onDone: (o: PoDto) => void }) {
  const open = order.lines.filter((l) => l.remaining > 0);
  const [qty, setQty] = useState<Record<string, number>>(Object.fromEntries(open.map((l) => [l.id, l.remaining])));
  const [docDate, setDocDate] = useState(todayInWarsaw());
  const [externalNumber, setExternalNumber] = useState('');
  const [price, setPrice] = useState<Record<string, number | null>>(Object.fromEntries(open.map((l) => [l.id, l.unitPriceGr])));
  // zamówienie w walucie obcej — kurs przyjęcia wymagany (wartość magazynu w PLN)
  const foreign = order.currency !== 'PLN';
  const [rate, setRate] = useState<number | null>(null);
  const key = useMemo(newKey, []);
  const m = useMutation({
    mutationFn: () =>
      api<PoDto>(`/purchase-orders/${order.id}/receive`, {
        method: 'POST',
        body: { docDate, externalNumber, exchangeRate: foreign ? rate : null, requestKey: key, lines: open.filter((l) => (qty[l.id] ?? 0) > 0).map((l) => ({ orderLineId: l.id, quantity: qty[l.id], unitPriceGr: price[l.id] ?? null })) },
      }),
    onSuccess: onDone,
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Dostawa do ${order.number}`} size="lg">
      <Stack>
        <Text size="sm" c="dimmed">
          Wpisz ilości faktycznie otrzymane (w jednostce magazynowej). Reszta zostaje w zamówieniu jako oczekiwana. Pusta cena — z zamówienia.
        </Text>
        <Group grow>
          <TextInput label="Data przyjęcia" type="date" max={todayInWarsaw()} value={docDate} onChange={(e) => setDocDate(e.currentTarget.value)} />
          <TextInput label="Nr dokumentu dostawcy (WZ)" value={externalNumber} onChange={(e) => setExternalNumber(e.currentTarget.value)} />
          {foreign && (
            <NumberInput
              label={`Kurs ${order.currency}/PLN`}
              min={0.000001}
              decimalScale={6}
              decimalSeparator=","
              value={rate ?? ''}
              onChange={(v) => setRate(Number(v) > 0 ? Number(v) : null)}
              error={!rate ? 'Podaj kurs większy od 0' : undefined}
            />
          )}
        </Group>
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Produkt</Table.Th>
              <Table.Th ta="right">Oczekiwane</Table.Th>
              <Table.Th w={140}>Otrzymano</Table.Th>
              <Table.Th w={130}>Cena / jedn.</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {open.map((l) => (
              <Table.Tr key={l.id}>
                <Table.Td>{l.name}</Table.Td>
                <Table.Td ta="right">
                  {qtyPl(l.remaining)} {l.unit}
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0} decimalScale={3} decimalSeparator="," value={qty[l.id] ?? 0} onChange={(v) => setQty({ ...qty, [l.id]: Number(v) || 0 })} aria-label={`Otrzymano: ${l.name}`} />
                </Table.Td>
                <Table.Td>
                  <MoneyInput size="xs" valueGr={price[l.id] ?? null} onChangeGr={(gr) => setPrice({ ...price, [l.id]: gr })} placeholder="z zamówienia" aria-label={`Cena: ${l.name}`} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {open.some((l) => (qty[l.id] ?? 0) > l.remaining) && (
          <Alert color="yellow" variant="light" p="xs">
            Ilość większa niż oczekiwana — nadwyżka trafi na stan jako zapas.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!open.some((l) => (qty[l.id] ?? 0) > 0) || (foreign && !rate)}>
            Przyjmij
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ReasonModal({ title, hint, path, onClose, onDone }: { title: string; hint: string; path: string; onClose: () => void; onDone: (o: PoDto) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({ mutationFn: () => api<PoDto>(path, { method: 'POST', body: { reason } }), onSuccess: onDone, onError: notifyError });
  return (
    <Modal opened onClose={onClose} title={title}>
      <Stack>
        <Text size="sm" c="dimmed">
          {hint}
        </Text>
        <Textarea label="Przyczyna" value={reason} onChange={(e) => setReason(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Wróć
          </Button>
          <Button color="red" onClick={() => m.mutate()} loading={m.isPending} disabled={reason.trim().length < 3}>
            Potwierdź
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type FormLine = { key: string; id: string | null; itemId: string | null; quantity: number; unitPriceGr: number | null; expectedDate: string; allocations: { demandLineId: string; quantity: number; label: string }[] };

/** Nowe / robocze zamówienie: pozycje z braków (z przypisaniem do potrzeb) albo ręczne (zapas). */
export type OrderDraft = { draft: true; warehouseId: string; supplierId: string | null; lines: FormLine[] };

function OrderForm({ order, draft, onClose, onSaved }: { order: PoDto | null; draft?: OrderDraft; onClose: () => void; onSaved: (o: PoDto) => void }) {
  const qc = useQueryClient();
  const sup = useSuppliers();
  const whs = useWarehouses();
  const items = useStockItems();
  const [supplierId, setSupplierId] = useState<string | null>(order?.supplierId ?? draft?.supplierId ?? null);
  const [warehouseId, setWarehouseId] = useState<string | null>(order?.warehouseId ?? draft?.warehouseId ?? null);
  const [expectedDate, setExpectedDate] = useState(order?.expectedDate ?? '');
  const [note, setNote] = useState(order?.note ?? '');
  const [lines, setLines] = useState<FormLine[]>(
    order?.lines.map((l) => ({
      key: l.id,
      id: l.id,
      itemId: l.itemId,
      quantity: l.quantity,
      unitPriceGr: l.unitPriceGr,
      expectedDate: l.expectedDate ?? '',
      allocations: l.allocations.filter((a) => a.demandLineId).map((a) => ({ demandLineId: a.demandLineId!, quantity: a.quantity, label: `${a.caseNumber ?? '—'}${a.taskNumber ? ` / ${a.taskNumber}` : ''}` })),
    })) ??
      draft?.lines ??
      [],
  );
  const shortages = useQuery({
    queryKey: ['demands', 'shortages', warehouseId],
    queryFn: () => api<ShortageByItemDto[]>(`/demands/shortages?warehouseId=${warehouseId}`),
    enabled: !!warehouseId,
  });
  const usedDemandLines = new Set(lines.flatMap((l) => l.allocations.map((a) => a.demandLineId)));
  const addShortage = (s: ShortageByItemDto) => {
    const allocs = s.lines.filter((x) => !usedDemandLines.has(x.lineId)).map((x) => ({ demandLineId: x.lineId, quantity: x.shortage, label: `${x.caseNumber ?? '—'}${x.taskNumber ? ` / ${x.taskNumber}` : ''}` }));
    if (!allocs.length) return;
    const qty = Math.round(allocs.reduce((a, x) => a + x.quantity, 0) * 1000) / 1000;
    const item = items.data?.find((i) => i.id === s.itemId);
    setLines((ls) => [...ls, { key: newKey(), id: null, itemId: s.itemId, quantity: qty, unitPriceGr: item?.purchasePriceGr ?? null, expectedDate: '', allocations: allocs }]);
  };
  const upd = (key: string, patch: Partial<FormLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const companyId = whs.data?.find((w) => w.id === warehouseId)?.companyId ?? null;
  const m = useMutation({
    mutationFn: () => {
      const body = {
        companyId,
        supplierId,
        warehouseId,
        expectedDate: expectedDate || null,
        note,
        lines: lines
          .filter((l) => l.itemId && l.quantity > 0)
          .map((l) => ({ id: l.id, itemId: l.itemId, quantity: l.quantity, unitPriceGr: l.unitPriceGr, expectedDate: l.expectedDate || null, allocations: l.allocations.map((a) => ({ demandLineId: a.demandLineId, quantity: a.quantity })) })),
      };
      return order ? api<PoDto>(`/purchase-orders/${order.id}`, { method: 'PUT', body }) : api<PoDto>('/purchase-orders', { method: 'POST', body });
    },
    onSuccess: (o) => {
      void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      void qc.invalidateQueries({ queryKey: ['demands'] });
      void qc.invalidateQueries({ queryKey: ['purchase-suggestions'] });
      void qc.invalidateQueries({ queryKey: ['shortages'] });
      qc.setQueryData(['purchase-order', o.id], o);
      notifyOk(order ? 'Zapisano zamówienie' : `Utworzono zamówienie ${o.number}`);
      onSaved(o);
    },
    onError: notifyError,
  });
  const openShortages = (shortages.data ?? []).filter((s) => s.lines.some((x) => !usedDemandLines.has(x.lineId)));
  return (
    <Modal opened onClose={onClose} title={order ? `Edycja ${order.number}` : 'Nowe zamówienie do dostawcy'} size="90rem">
      <Stack>
        <Group grow align="flex-start">
          <Select
            label="Dostawca"
            searchable
            data={(sup.data ?? []).filter((s) => s.active || s.id === supplierId).map((s) => ({ value: s.id, label: s.nip ? `${s.name} (${s.nip})` : s.name }))}
            value={supplierId}
            onChange={setSupplierId}
            nothingFoundMessage="Dodaj dostawcę w zakładce „Dostawcy”"
          />
          <WarehouseSelect label="Magazyn docelowy" value={warehouseId} onChange={setWarehouseId} disabled={!!order} />
          <TextInput label="Oczekiwany termin" type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.currentTarget.value)} />
        </Group>
        {warehouseId && (
          <SectionCard title="Braki w zapotrzebowaniach" padding="sm">
            <QueryState isLoading={shortages.isLoading} error={shortages.error} lines={2}>
              {openShortages.length === 0 ? (
                <Text size="sm" c="dimmed">
                  Brak niezabezpieczonych braków w tym magazynie.
                </Text>
              ) : (
                <Table verticalSpacing={4}>
                  <Table.Tbody>
                    {openShortages.map((s) => (
                      <Table.Tr key={s.itemId}>
                        <Table.Td>
                          <Text size="sm">{s.name}</Text>
                          <Text size="xs" c="dimmed">
                            {s.lines.map((x) => `${x.caseNumber ?? '—'}${x.taskNumber ? ` / ${x.taskNumber}` : ''}: ${qtyPl(x.shortage)}`).join(' · ')}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          brak {qtyPl(s.shortage)} {s.unit}
                        </Table.Td>
                        <Table.Td>{s.earliestNeededBy ? `na ${datePl(s.earliestNeededBy)}` : ''}</Table.Td>
                        <Table.Td w={90}>
                          <Button size="compact-xs" variant="light" onClick={() => addShortage(s)}>
                            Dodaj
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </QueryState>
          </SectionCard>
        )}
        <SectionCard
          title="Pozycje"
          padding="sm"
          actions={
            <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => setLines((ls) => [...ls, { key: newKey(), id: null, itemId: null, quantity: 1, unitPriceGr: null, expectedDate: '', allocations: [] }])}>
              Pozycja ręczna (zapas)
            </Button>
          }
        >
          {lines.length === 0 ? (
            <Text size="sm" c="dimmed">
              Dodaj braki z listy powyżej albo pozycję ręczną.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={800}>
              <Table verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Produkt</Table.Th>
                    <Table.Th w={120}>Ilość</Table.Th>
                    <Table.Th w={130}>Cena / jedn.</Table.Th>
                    <Table.Th w={150}>Termin pozycji</Table.Th>
                    <Table.Th w={40} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {lines.map((l) => {
                    const allocated = l.allocations.reduce((a, x) => a + x.quantity, 0);
                    return (
                      <Table.Tr key={l.key}>
                        <Table.Td>
                          <ItemSelect size="xs" items={items.data ?? []} value={l.itemId} onChange={(v) => upd(l.key, { itemId: v, allocations: [] })} disabled={l.allocations.length > 0} />
                          <Text size="xs" c="dimmed">
                            {l.allocations.length ? `dla: ${l.allocations.map((a) => `${a.label} (${qtyPl(a.quantity)})`).join(', ')}` : 'na zapas'}
                            {l.quantity > allocated && l.allocations.length > 0 ? ` · zapas ${qtyPl(l.quantity - allocated)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <NumberInput size="xs" min={Math.max(0.001, allocated)} decimalScale={3} decimalSeparator="," value={l.quantity} onChange={(v) => upd(l.key, { quantity: Number(v) || 0 })} />
                        </Table.Td>
                        <Table.Td>
                          <MoneyInput size="xs" valueGr={l.unitPriceGr} onChangeGr={(gr) => upd(l.key, { unitPriceGr: gr })} aria-label="Cena za jednostkę" />
                        </Table.Td>
                        <Table.Td>
                          <TextInput size="xs" type="date" value={l.expectedDate} onChange={(e) => upd(l.key, { expectedDate: e.currentTarget.value })} />
                        </Table.Td>
                        <Table.Td>
                          <Button size="compact-xs" variant="subtle" color="red" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} aria-label="Usuń pozycję">
                            ×
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </SectionCard>
        <Textarea label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={1} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={!supplierId || !warehouseId || !companyId || !lines.some((l) => l.itemId && l.quantity > 0)}>
            Zapisz roboczy
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SuppliersSection({ me }: { me: MeDto }) {
  const q = useSuppliers();
  const [edit, setEdit] = useState<SupplierDto | 'new' | null>(null);
  const canEdit = can(me, 'dostawcy.edycja');
  return (
    <SectionCard
      title="Dostawcy"
      padding={0}
      actions={
        canEdit && (
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEdit('new')}>
            Dodaj dostawcę
          </Button>
        )
      }
    >
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data?.length === 0 ? (
          <EmptyState compact title="Brak dostawców" hint="Dostawca dodaje się też sam przy przyjęciu z faktury KSeF (po NIP)." />
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nazwa</Table.Th>
                <Table.Th>NIP</Table.Th>
                <Table.Th>Kontakt</Table.Th>
                <Table.Th ta="right">Czas dostawy</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {q.data?.map((s) => (
                <Table.Tr key={s.id} style={{ cursor: canEdit ? 'pointer' : undefined, opacity: s.active ? 1 : 0.5 }} onClick={() => canEdit && setEdit(s)}>
                  <Table.Td>{s.name}</Table.Td>
                  <Table.Td>{s.nip || '—'}</Table.Td>
                  <Table.Td>{[s.email, s.phone].filter(Boolean).join(' · ') || '—'}</Table.Td>
                  <Table.Td ta="right">{s.leadTimeDays === null ? '—' : `${s.leadTimeDays} dni rob.`}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </QueryState>
      {edit && <SupplierModal supplier={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </SectionCard>
  );
}

function SupplierModal({ supplier, onClose }: { supplier: SupplierDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<SupplierInput>(supplier ?? { name: '', nip: '', email: '', phone: '', leadTimeDays: null, note: '', active: true });
  const m = useMutation({
    mutationFn: () => api<SupplierDto[]>(supplier ? `/suppliers/${supplier.id}` : '/suppliers', { method: supplier ? 'PUT' : 'POST', body: v }),
    onSuccess: (r) => (qc.setQueryData(['suppliers'], r), notifyOk('Zapisano dostawcę'), onClose()),
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={supplier ? 'Dostawca' : 'Nowy dostawca'}>
      <Stack>
        <TextInput label="Nazwa" value={v.name} onChange={(e) => setV({ ...v, name: e.currentTarget.value })} />
        <Group grow>
          <TextInput label="NIP" value={v.nip} onChange={(e) => setV({ ...v, nip: e.currentTarget.value })} />
          <NumberInput label="Czas dostawy (dni rob.)" min={0} max={365} value={v.leadTimeDays ?? ''} onChange={(x) => setV({ ...v, leadTimeDays: x === '' ? null : Number(x) })} />
        </Group>
        <Group grow>
          <TextInput label="E-mail" value={v.email} onChange={(e) => setV({ ...v, email: e.currentTarget.value })} />
          <TextInput label="Telefon" value={v.phone} onChange={(e) => setV({ ...v, phone: e.currentTarget.value })} />
        </Group>
        <Textarea label="Uwagi" value={v.note} onChange={(e) => setV({ ...v, note: e.currentTarget.value })} autosize minRows={1} />
        <Switch label="Aktywny" checked={v.active} onChange={(e) => setV({ ...v, active: e.currentTarget.checked })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending} disabled={v.name.trim().length < 2}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}


/** Podpowiedzi zakupowe (docs/14 §2.11): wyjaśnione wyliczenie i „zamów do”; zamówienie tworzy się świadomie z zaznaczonych. */
function SuggestionsSection({ canOrder, onOrder }: { canOrder: boolean; onOrder: (d: OrderDraft) => void }) {
  const q = useQuery({ queryKey: ['purchase-suggestions'], queryFn: () => api<PurchaseSuggestionDto[]>('/purchase-suggestions') });
  const [sel, setSel] = useState<string[]>([]);
  const today = todayInWarsaw();
  const k = (s: PurchaseSuggestionDto) => `${s.warehouseId}|${s.itemId}`;
  const chosen = (q.data ?? []).filter((s) => sel.includes(k(s)));
  const oneWarehouse = new Set(chosen.map((s) => s.warehouseId)).size === 1;
  const order = () => {
    const lines: FormLine[] = chosen.map((s) => {
      // przypisanie do potrzeb — najwcześniejsze terminy, do ilości „na realizacje”; reszta pozycji to zapas (minimum)
      let left = s.forDemands;
      const allocations = [...s.lines]
        .sort((a, b) => (a.neededBy ?? '9999').localeCompare(b.neededBy ?? '9999'))
        .flatMap((l) => {
          const n = Math.min(l.shortage, left);
          left = Math.round((left - n) * 1000) / 1000;
          return n > 0 ? [{ demandLineId: l.lineId, quantity: n, label: `${l.caseNumber ?? '—'}${l.taskNumber ? ` / ${l.taskNumber}` : ''}` }] : [];
        });
      return { key: newKey(), id: null, itemId: s.itemId, quantity: s.quantity, unitPriceGr: null, expectedDate: '', allocations };
    });
    onOrder({ draft: true, warehouseId: chosen[0]!.warehouseId, supplierId: chosen.find((s) => s.supplierId)?.supplierId ?? null, lines });
    setSel([]);
  };
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data?.length === 0 ? (
        <EmptyState title="Brak podpowiedzi" hint="Braki potwierdzonych realizacji i stany poniżej minimum są pokryte towarem albo zamówieniami." />
      ) : (
        <SectionCard
          padding={0}
          title="Co zamówić"
          actions={
            canOrder && (
              <Button size="xs" disabled={!chosen.length || !oneWarehouse} onClick={order} title={chosen.length && !oneWarehouse ? 'Zaznacz pozycje jednego magazynu' : undefined}>
                Utwórz zamówienie z zaznaczonych ({chosen.length})
              </Button>
            )
          }
        >
          <Table.ScrollContainer minWidth={820}>
            <Table verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>Produkt</Table.Th>
                  <Table.Th>Magazyn</Table.Th>
                  <Table.Th ta="right">Zamów</Table.Th>
                  <Table.Th>Zamów do</Table.Th>
                  <Table.Th>Dostawca</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data?.map((s) => (
                  <Table.Tr key={k(s)}>
                    <Table.Td>
                      {canOrder && <Checkbox checked={sel.includes(k(s))} onChange={(e) => setSel(e.currentTarget.checked ? [...sel, k(s)] : sel.filter((x) => x !== k(s)))} aria-label={`Zaznacz: ${s.name}`} />}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {s.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {s.explanation}
                      </Text>
                      <Text size="xs" c="dimmed">
                        dostępne {qtyPl(s.available)} · zapas w zamówieniach {qtyPl(s.incomingFree)}
                        {s.minQty !== null ? ` · minimum ${qtyPl(s.minQty)}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>{s.warehouseCode}</Table.Td>
                    <Table.Td ta="right" fw={700}>
                      {qtyPl(s.quantity)} {s.unit}
                    </Table.Td>
                    <Table.Td>
                      {s.orderBy ? (
                        <Text size="sm" c={s.orderBy <= today ? 'red' : undefined} fw={s.orderBy <= today ? 700 : 400}>
                          {datePl(s.orderBy)}
                        </Text>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>{s.supplierName ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </SectionCard>
      )}
    </QueryState>
  );
}
